import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import './Messages.css';

export default function Messages() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const [convs,    setConvs]    = useState([]);
  const [selected, setSelected] = useState(null);
  const [msgs,     setMsgs]     = useState([]);
  const [reply,    setReply]    = useState('');
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetchConvs();
  }, [user]);

  useEffect(() => { if (selected) fetchMsgs(selected); }, [selected]);

  async function fetchConvs() {
    const { data } = await supabase
      .from('messages')
      .select('*, listings(id,title,images), sender:profiles!messages_sender_id_fkey(username), receiver:profiles!messages_receiver_id_fkey(username)')
      .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
      .order('created_at', { ascending: false });

    if (data) {
      const map = {};
      data.forEach(m => {
        const otherId   = m.sender_id === user.id ? m.receiver_id : m.sender_id;
        const otherName = m.sender_id === user.id ? m.receiver?.username : m.sender?.username;
        const key = `${m.listing_id}-${otherId}`;
        if (!map[key]) map[key] = { key, listingId: m.listing_id, listing: m.listings, otherId, otherName, lastMsg: m, unread: 0 };
        if (!m.is_read && m.receiver_id === user.id) map[key].unread++;
      });
      const list = Object.values(map);
      setConvs(list);
      if (list.length > 0) setSelected(list[0].key);
    }
    setLoading(false);
  }

  async function fetchMsgs(key) {
    const conv = convs.find(c => c.key === key) || null;
    if (!conv) return;
    const { data } = await supabase
      .from('messages')
      .select('*, sender:profiles!messages_sender_id_fkey(username)')
      .eq('listing_id', conv.listingId)
      .or(`and(sender_id.eq.${user.id},receiver_id.eq.${conv.otherId}),and(sender_id.eq.${conv.otherId},receiver_id.eq.${user.id})`)
      .order('created_at', { ascending: true });
    setMsgs(data || []);
    await supabase.from('messages').update({ is_read: true }).eq('receiver_id', user.id).eq('listing_id', conv.listingId);
  }

  async function sendReply(e) {
    e.preventDefault();
    if (!reply.trim() || !selected) return;
    const conv = convs.find(c => c.key === selected);
    await supabase.from('messages').insert({ listing_id: conv.listingId, sender_id: user.id, receiver_id: conv.otherId, content: reply.trim() });
    setReply('');
    fetchMsgs(selected);
  }

  const selConv = convs.find(c => c.key === selected);
  if (loading) return <div className="spinner" />;

  return (
    <div className="messages-page page-enter">
      <div className="container">
        <h1 className="messages-title">Messages</h1>
        {convs.length === 0 ? (
          <div className="empty-state"><h3>No messages yet</h3><p>When you message a seller, conversations appear here.</p></div>
        ) : (
          <div className="messages-layout">
            <div className="conv-list">
              {convs.map(c => (
                <button key={c.key} className={`conv-item ${selected === c.key ? 'active' : ''}`} onClick={() => setSelected(c.key)}>
                  <div className="conv-av">{c.otherName?.[0]?.toUpperCase()}</div>
                  <div className="conv-meta">
                    <div className="conv-top">
                      <span className="conv-user">{c.otherName}</span>
                      {c.unread > 0 && <span className="badge badge-violet">{c.unread}</span>}
                    </div>
                    <p className="conv-listing">{c.listing?.title}</p>
                    <p className="conv-preview">{c.lastMsg?.content}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="thread">
              {selConv && <>
                <div className="thread-head">
                  <p className="thread-user">{selConv.otherName}</p>
                  <a href={`/listings/${selConv.listingId}`} className="thread-listing-link">{selConv.listing?.title}</a>
                </div>
                <div className="thread-msgs">
                  {msgs.map(m => (
                    <div key={m.id} className={`bubble ${m.sender_id === user.id ? 'mine' : 'theirs'}`}>
                      <p>{m.content}</p>
                      <span className="bubble-time">{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <form className="thread-reply" onSubmit={sendReply}>
                  <textarea className="form-textarea" placeholder="Write a reply..." value={reply} onChange={e => setReply(e.target.value)} rows={3} />
                  <button type="submit" className="btn btn-primary" disabled={!reply.trim()}>Send</button>
                </form>
              </>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
